/**
 * Parse model output that may be a JSON string, a fenced ```json block, or
 * already an object. Returns null when nothing parseable is present.
 */
export const tryParseJson = (text) => {
  if (text && typeof text === "object") {
    return text;
  }
  const raw = String(text || "").trim();
  if (!raw) {
    return null;
  }
  const stripped = raw
    .replace(/^```json\s*/i, "")
    .replace(/\s*```$/g, "")
    .trim();
  try {
    return JSON.parse(raw);
  } catch {
    // fall through to stripped attempt
  }
  try {
    return JSON.parse(stripped);
  } catch {
    return null;
  }
};

/**
 * Tool-loop baselines wrap the answer in `{ text, toolCalls }` where `text`
 * is itself JSON. Plain replays return the payload directly. Unwrap both to
 * the same level or baseline fields read as undefined.
 */
export const unwrapStageOutput = (output) => {
  const rawText =
    typeof output === "string" ? output : JSON.stringify(output ?? "");
  const parsed = tryParseJson(output);
  if (!parsed || typeof parsed !== "object") {
    return { json: null, text: String(rawText || ""), toolCalls: [] };
  }
  const isEnvelope =
    typeof parsed.text === "string" &&
    (Object.prototype.hasOwnProperty.call(parsed, "toolCalls") ||
      Object.keys(parsed).length === 1);
  if (!isEnvelope) {
    return { json: parsed, text: String(rawText || ""), toolCalls: [] };
  }
  const inner = tryParseJson(parsed.text);

  return {
    json: inner && typeof inner === "object" ? inner : null,
    text: parsed.text,
    toolCalls: (Array.isArray(parsed.toolCalls) ? parsed.toolCalls : [])
      .map((call) => String(call?.name || call?.tool || call || ""))
      .filter(Boolean),
  };
};

/**
 * Models disagree about which field carries the Step 1 write-up. Preview
 * whichever one actually holds the body and say which it was.
 */
export const pickStep1Body = (payload) => {
  const result = String(payload?.result || "");
  const reason = String(payload?.reason || "");
  if (!result && !reason) {
    return { field: null, text: "" };
  }

  return result.length >= reason.length
    ? { field: "result", text: result }
    : { field: "reason", text: reason };
};

export const formatMoney = (value) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) {
    return "—";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number(value));
};

export const formatPercent = (value) => {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }

  return `${(Number(value) * 100).toFixed(1)}%`;
};

export const formatDelta = (delta) => {
  if (delta == null || Number.isNaN(Number(delta))) {
    return "—";
  }
  const n = Number(delta);
  const sign = n > 0 ? "+" : "";

  return `${sign}${formatMoney(n)}`;
};

/** Intersection of two string arrays (order-stable from left). */
export const intersectIds = (left = [], right = []) => {
  const rightSet = new Set(right.map(String));

  return left.map(String).filter((id) => rightSet.has(id));
};

/**
 * Step 3 is only meaningful for a model-switch decision when it consumes the
 * candidate Step 1 output. Keep Step 1 selected whenever Step 3 is enabled.
 */
export const toggleStageSelection = (currentStages, stageId) => {
  const current = Array.isArray(currentStages) ? currentStages : [];
  if (
    stageId === "pcStep1" &&
    current.includes("pcStep1") &&
    current.includes("pcStep3")
  ) {
    return current;
  }
  if (current.includes(stageId)) {
    return current.filter((id) => id !== stageId);
  }
  if (stageId === "pcStep3") {
    return [...new Set([...current, "pcStep1", "pcStep3"])];
  }

  return [...current, stageId];
};

/** Firestore docs sometimes store classification as a structured object. */
export const formatDocClassification = (value) => {
  if (value == null || value === "") {
    return null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();

    return trimmed || null;
  }
  if (typeof value === "object") {
    const parts = [
      value.documentType,
      value.issuingEntity,
      value.year,
    ].filter(Boolean);
    if (parts.length > 0) {
      return parts.join(" · ");
    }

    return null;
  }

  return String(value);
};

export const STAGE_LABELS = {
  classification: "Classification",
  docSelection: "Doc selection",
  pcStep1: "PC Step 1",
  pcStep3: "PC Step 3",
};

export const STAGE_ORDER = [
  "classification",
  "docSelection",
  "pcStep1",
  "pcStep3",
];
